import React, { useState, useEffect, useContext } from "react"
import { auth, db } from "../../../utils/firebase"
import { AuthContext } from "../../../utils/providers/auth"
import { readTextToParse } from "../../../utils/enquiries/to-parse"
import firebase from "firebase"
import moment from "moment"

// UI components & structure
import Input from "../../../components/ui/inputs/input"
import Select from "../../../components/ui/select/select"
import Textarea from "../../../components/ui/inputs/textarea"
import Window from "../../../components/structure/window/window"
import WindowCard from "../../../components/ui/window-card/window-card"
import Button from "../../../components/ui/button/button"
import Datepicker from "../../../components/ui/datepicker/datepicker"
import { LoadingIcon } from "../../../utils/svgs"

// Modal for adding a new enquiry
export default function Add(props) {
	const [saving, setSaving] = useState(false)
	const [parsing, setParsing] = useState(false)
	const [canSave, setCanSave] = useState(false)
	const [showEmailParser, setShowEmailParser] = useState(false)
	const [emailText, setEmailText] = useState("")

	// Client fields
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [phone, setPhone] = useState("")
	const [email, setEmail] = useState("")

	// Enquiry fields
	const [location, setLocation] = useState(null)
	const [groupSize, setGroupSize] = useState(null)
	const [checkIn, setCheckIn] = useState(null)
	const [nights, setNights] = useState(null)
	const [rounds, setRounds] = useState(null)
	const [quoted, setQuoted] = useState(null)
	const [notes, setNotes] = useState(null)
	const [channel, setChannel] = useState(null)
	const [type, setType] = useState("BEING_PROCESSED")

	// Tracking fields
	const [utmSource, setUtmSource] = useState(null)
	const [utmCampaign, setUtmCampaign] = useState(null)
	const [utmMedium, setUtmMedium] = useState(null)
	const [utmTerm, setUtmTerm] = useState(null)
	const [utmContent, setUtmContent] = useState(null)

	// Deconstruct the client ID from the props
	const { clientID } = props

	// Get the current user from context
	const { user } = useContext(AuthContext)

	// Check URL parameters on component mount and populate form fields
	useEffect(() => {
		// Check if we have a to_parse parameter for OpenAI parsing
		const textToParse = readTextToParse()
		if (textToParse) {
			parseEnquiryText(textToParse)
			return
		}
	}, [])

	// Check if the form can be saved
	useEffect(() => {
		let canSave = true

		if (!firstName) canSave = false
		if (!lastName) canSave = false
		if (!phone) canSave = false
		if (!email) canSave = false
		if (!location) canSave = false
		if (!groupSize && groupSize !== 0) canSave = false
		if (!checkIn) canSave = false
		if (!nights && nights !== 0) canSave = false
		if (!rounds && rounds !== 0) canSave = false
		if (!quoted && quoted !== 0) canSave = false
		if (!notes) canSave = false

		setCanSave(canSave)
	}, [firstName, lastName, phone, email, location, groupSize, checkIn, nights, rounds, quoted, notes])

	// Parse enquiry text using OpenAI API
	const parseEnquiryText = async (text) => {
		setParsing(true)
		try {
			// Send the text to the OpenAI API via our cloud function
			const response = await fetch("https://us-central1-ghd-booking-system.cloudfunctions.net/parseEnquiryText", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ text }),
			})

			// If there was an error, throw an error
			if (!response.ok) {
				throw new Error(`Function error in response: ${response.status}`)
			}

			// Get the result from the response
			const result = await response.json()

			// If the status is 200 and there is data, populate form fields
			if (result.status === 200 && result.data) {
				// Get the enquiry details from the response
				const data = result.data.enquiry_details

				// Populate form fields with parsed data
				if (data.firstName) setFirstName(data.firstName)
				if (data.lastName) setLastName(data.lastName)
				if (data.phone) setPhone(data.phone)
				if (data.email) setEmail(data.email)
				if (data.location) setLocation(data.location)
				if (data.groupSize) {
					setGroupSize(data.groupSize)
				} else {
					setGroupSize(0)
				}
				if (data.checkIn) {
					// Convert the checkIn date to a milliseconds timestamp
					let checkInDate = moment(data.checkIn, "YYYY-MM-DD").valueOf()

					// While the check in date is in the past, add a year
					while (checkInDate < moment().valueOf()) {
						checkInDate = moment(checkInDate).add(1, "year").valueOf()
					}

					// Set the check in date
					setCheckIn(checkInDate)
				} else {
					setCheckIn(null)
				}
				if (data.nights) {
					setNights(data.nights)
				} else {
					setNights(0)
				}
				if (data.rounds) {
					setRounds(data.rounds)
				} else {
					setRounds(0)
				}
				if (data.quoted) {
					setQuoted(data.quoted)
				} else {
					setQuoted(0)
				}
				if (data.notes) setNotes(data.notes)
				if (data.utmSource?.length > 0) setUtmSource(data.utmSource || null)
				if (data.utmCampaign?.length > 0) setUtmCampaign(data.utmCampaign || null)
				if (data.utmMedium?.length > 0) setUtmMedium(data.utmMedium || null)
				if (data.utmTerm?.length > 0) setUtmTerm(data.utmTerm || null)
				if (data.utmContent?.length > 0) setUtmContent(data.utmContent || null)

				// Set channel if provided, otherwise default to EMAIL
				if (data.channel) {
					setChannel(data.channel)
				} else {
					setChannel("EMAIL")
				}

				// Then set type to REQUIRE_ATTENTION to land in the pot
				setType("REQUIRE_ATTENTION")
			}
		} catch (error) {
			// Show an error message to the user
			console.log("Error parsing enquiry text:", error)
		} finally {
			setParsing(false)
		}
	}

	/**
	 * Save a new enquiry into the database
	 */
	const saveNewEnquiry = async () => {
		/**
		 * Show a loading spinner
		 */
		setSaving(true)
		/**
		 * Generate a date_from tiemstamp from the checkIn date milliseconds
		 */
		const date_from = firebase.firestore.Timestamp.fromMillis(checkIn)
		/**
		 * Do we already have a clientID from the props?
		 */
		if (clientID) {
			/**
			 * Then add a new enquiry
			 */
			await db.collection("enquiries").add({
				agent: type === "REQUIRE_ATTENTION" ? null : auth.currentUser.uid,
				client: clientID,
				created: firebase.firestore.FieldValue.serverTimestamp(),
				date_from,
				group_size: Number(groupSize),
				location,
				nights: Number(nights),
				rounds: Number(rounds),
				quoted: Number.parseFloat(quoted),
				notes,
				site: "SYSTEM",
				inbound_channel: channel,
				status: type,
				tracking: {
					utm_source: utmSource || null,
					utm_campaign: utmCampaign || null,
					utm_medium: utmMedium || null,
					utm_term: utmTerm || null,
					utm_content: utmContent || null,
				},
			})
		} else {
			/**
			 * Check for a client account with the same email
			 */
			const newClientID = await db
				.collection("clients")
				.where("email", "==", email)
				.get()
				.then(async (clientDocs) => {
					/**
					 * If there was a client document found
					 */
					if (clientDocs.size > 0) {
						/**
						 * Return the ID fo the first document
						 */
						return clientDocs.docs[0].id
					} else {
						/**
						 * If they don't exist we need to make a new record
						 */
						const newAccountID = await db
							.collection("clients")
							.add({
								first_name: firstName,
								last_name: lastName,
								email,
								phone,
								created: firebase.firestore.FieldValue.serverTimestamp(),
							})
							.then((newClientDoc) => {
								return newClientDoc.id
							})
						/**
						 * Then return this new account ID
						 */
						return newAccountID
					}
				})
			/**
			 * Then add a new enquiry
			 */
			await db.collection("enquiries").add({
				agent: type === "REQUIRE_ATTENTION" ? null : auth.currentUser.uid,
				client: newClientID,
				created: firebase.firestore.FieldValue.serverTimestamp(),
				date_from,
				group_size: Number(groupSize),
				location,
				nights: Number(nights),
				rounds: Number(rounds),
				quoted: Number.parseFloat(quoted),
				notes,
				site: "SYSTEM",
				inbound_channel: channel,
				status: type,
				tracking: {
					utm_source: utmSource || null,
					utm_campaign: utmCampaign || null,
					utm_medium: utmMedium || null,
					utm_term: utmTerm || null,
					utm_content: utmContent || null,
				},
			})
		}
		/**
		 * Reset the state and close the window
		 */
		setSaving(false)
		props.close()
	}

	return (
		<Window
			title="Add a new enquiry"
			close={() => props.close()}>
			{/* Show parsing spinner when parsing enquiry text */}
			{parsing && (
				<div className="table-loading-splash enquiry-window">
					<div className="loading-wrapper">
						<p>Parsing enquiry body...</p>
						<div className="svg-loading-wrap">
							<LoadingIcon />
						</div>
					</div>
				</div>
			)}

			{!clientID && (
				<WindowCard
					title="Client details"
					className="has-top-margin">
					<div className="input-grid cols-2">
						<Input
							type="text"
							label="First name:"
							value={firstName}
							onChange={setFirstName}
							placeholder="First name:"
						/>
						<Input
							type="text"
							label="Last name:"
							value={lastName}
							onChange={setLastName}
							placeholder="Last name:"
						/>
						<Input
							type="tel"
							label="Phone:"
							value={phone}
							onChange={setPhone}
							placeholder="Phone:"
						/>
						<Input
							type="email"
							label="Email:"
							value={email}
							onChange={setEmail}
							placeholder="Email:"
						/>
					</div>
				</WindowCard>
			)}

			<WindowCard
				title="Enquiry details"
				className="has-top-margin">
				<div className="input-grid cols-4">
					<Input
						type="text"
						label="Location:"
						value={location}
						onChange={setLocation}
						placeholder="Location:"
						wrapperClass="span-2"
					/>
					<Datepicker
						type="text"
						label="Check in:"
						value={checkIn}
						onSelect={(date) => setCheckIn(date)}
						placeholder="Check in:"
						wrapperClass="span-2"
					/>
					<Input
						type="number"
						label="Group size:"
						value={groupSize}
						onChange={setGroupSize}
						placeholder="Group size:"
					/>
					<Input
						type="number"
						label="Nights:"
						value={nights}
						onChange={setNights}
						placeholder="Nights:"
					/>
					<Input
						type="number"
						label="Rounds:"
						value={rounds}
						onChange={setRounds}
						placeholder="Rounds:"
					/>
					<Input
						type="number"
						label="Quoted:"
						value={quoted}
						onChange={setQuoted}
						placeholder="Quoted:"
						symbol="£"
					/>

					<div className="span-4">
						<Textarea
							placeholder="Notes:"
							value={notes}
							onChange={setNotes}
						/>
					</div>

					<div className="span-4">
						<Select
							value={channel}
							placeholder="Channel:"
							selected={channel}
							onSelect={(option) => setChannel(option.option)}
							options={{
								EMAIL: "Sales@ Email",
								EMAI_MARKETING: "Marketing@ Email",
								FACEBOOK: "Facebook Messenger",
								INSTAGRAM: "Instagram DM",
							}}
						/>
					</div>

					<div className="span-4">
						<Select
							value={type}
							placeholder="Lead type:"
							selected={type}
							onSelect={(option) => setType(option.option)}
							options={{
								HOT: "Hot",
								FREEPHONE: "Freephone",
								BEING_PROCESSED: "Processing",
								REQUIRE_ATTENTION: "Require Attention",
							}}
						/>
					</div>
				</div>
			</WindowCard>

			<WindowCard
				title="Tracking details"
				className="has-top-margin">
				<div className="input-grid cols-2">
					<Input
						type="text"
						label="UTM Source:"
						value={utmSource}
						onChange={setUtmSource}
						placeholder="UTM Source:"
						wrapperClass="span-2"
					/>
					<Input
						type="text"
						label="UTM Campaign:"
						value={utmCampaign}
						onChange={setUtmCampaign}
						placeholder="UTM Campaign:"
					/>
					<Input
						type="text"
						label="UTM Medium:"
						value={utmMedium}
						onChange={setUtmMedium}
						placeholder="UTM Medium:"
					/>
					<Input
						type="text"
						label="UTM Term:"
						value={utmTerm}
						onChange={setUtmTerm}
						placeholder="UTM Term:"
					/>
					<Input
						type="text"
						label="UTM Content:"
						value={utmContent}
						onChange={setUtmContent}
						placeholder="UTM Content:"
					/>
				</div>
			</WindowCard>

			{/* If there is a clientID present, we only need to check enquiry details */}
			{clientID && (
				<Button
					label="Save new enquiry"
					loading={saving}
					loadingText="Saving..."
					onClick={() => saveNewEnquiry()}
					className="window-card-button"
					disabled={!clientID || !location || !groupSize || !checkIn || !nights || !rounds || !quoted || !notes}
				/>
			)}

			{/* If there is no clientID present, check for client details too */}
			{!clientID && (
				<Button
					label="Save new enquiry"
					loading={saving}
					loadingText="Saving..."
					onClick={() => saveNewEnquiry()}
					className="window-card-button"
					disabled={!canSave}
				/>
			)}

			{user?.is_admin && (
				<WindowCard>
					{!showEmailParser ? (
						<Button
							small={true}
							label="Parse Email Body"
							onClick={() => setShowEmailParser(true)}
							className="window-card-button"
						/>
					) : (
						<div>
							<Textarea
								placeholder="Paste the email body..."
								value={emailText}
								onChange={setEmailText}
								rows={10}
							/>
							<div
								className="input-grid cols-2"
								style={{ marginTop: "10px" }}>
								<Button
									small={true}
									label="Parse Enquiry"
									onClick={() => {
										parseEnquiryText(emailText)
										setShowEmailParser(false)
										setEmailText("")
									}}
									disabled={!emailText}
								/>
								<Button
									small={true}
									label="Cancel"
									onClick={() => {
										setShowEmailParser(false)
										setEmailText("")
									}}
								/>
							</div>
						</div>
					)}
				</WindowCard>
			)}
		</Window>
	)
}
